/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { debounce } from 'lodash';
import { CheckCircle } from 'lucide-react';
import { ValidadeService } from '../../services/validade.service';
interface ModalCodeProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
}

export function ReferralModal({ isOpen, onClose, onConfirm }: ModalCodeProps) {
  const [isValid, setIsValid] = React.useState<boolean | null>(null);
  const [checking, setChecking] = React.useState(false);
  const [code, setCode] = React.useState('');
  const [touched, setTouched] = React.useState(false);

  const isEmpty = touched && code.trim() === '';
  const isValidFormat = /^[A-Z0-9]{5}-\d{4}$/.test(code);
  const showSuccess = touched && code && isValidFormat && isValid;
  const showError = touched && (!isValidFormat || isEmpty || isValid === false);

  const handleChange = (value: string) => {
    setCode(value.toUpperCase());
    if (!touched) setTouched(true);
  };

  const handleSubmit = async () => {
    if (!isValidFormat) return;

    setChecking(true);
    const valid = await ValidadeService.validade(code);
    setChecking(false);
    setIsValid(valid);

    if (valid) {
      onConfirm(code);
    }
  };

  useEffect(() => {
    if (isValidFormat) {
      const validateCode = debounce(async () => {
        setChecking(true);
        const valid = await ValidadeService.validade(code);
        setIsValid(valid);
        setChecking(false);
      }, 500);

      validateCode();

      return () => validateCode.cancel();
    } else {
      setIsValid(null);
    }
  }, [code]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/30 flex items-center justify-center px-4">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-lg relative">
        {/* Botão de fechar */}
        <button className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 text-xl" onClick={onClose}>
          &times;
        </button>

        {/* Título */}
        <h2 className="text-lg font-semibold text-gray-900 mb-1">Alguém te indicou o Clubinho?</h2>
        <p className="text-sm text-gray-500 mb-5">
          Digite o código de quem te indicou: você recebe um presente no seu primeiro pacote e essa pessoa também é
          presenteada. Esse código não é cupom de desconto.
        </p>

        {/* Campo de input */}
        <div className="mb-6">
          <label className="block text-sm text-gray-700 mb-2" htmlFor="referralCode">
            Código de indicação
          </label>
          <div className="relative">
            <input
              id="referralCode"
              type="text"
              value={code}
              onChange={(e) => handleChange(e.target.value)}
              className={`w-full h-12 px-4 py-2 pr-10 text-sm text-gray-700 placeholder-gray-400 border rounded-md focus:outline-none ${
                showError ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-blue-500'
              }`}
              placeholder="Digite o código"
            />
            {showSuccess && <CheckCircle className="w-5 h-5 text-green-500 absolute right-3 top-3" />}
            {isEmpty && <p className="text-red-500 text-sm mt-1">Preencha esse campo.</p>}
            {!isEmpty && touched && !isValidFormat && (
              <p className="text-red-500 text-sm mt-1">Código inválido. Formato esperado: XXXXX-1234</p>
            )}
            {touched && isValid === false && isValidFormat && (
              <p className="text-red-500 text-sm mt-1">Esse código não é válido ou não foi encontrado.</p>
            )}
          </div>
        </div>

        {/* Botões */}
        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            className="flex gap-2 items-center justify-start hover:opacity-90 hover:cursor-pointer text-[#9195A1] px-3 py-2 rounded-md"
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={!isValidFormat || checking}
            className={`px-3 py-2 rounded-md text-white ${
              isValidFormat && !checking
                ? 'bg-[#018ED5] hover:opacity-90 hover:cursor-pointer'
                : 'bg-gray-300 cursor-not-allowed'
            }`}
          >
            {checking ? 'Verificando...' : 'Adicionar'}
          </button>
        </div>
      </div>
    </div>
  );
}
